// src/pages/Home.js
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccess } from '../contexts/AccessContext';
import { ROLE_LABELS } from '../config/roles';

// Collapse multi-column layouts to a single column on phones.
function useIsMobile(breakpoint = 720) {
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== 'undefined' && window.innerWidth < breakpoint
  );
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

const s = {
  page: {},
  welcomeBanner: {
    background: 'linear-gradient(135deg, var(--ka-navy) 0%, var(--ka-navy-mid) 100%)',
    borderRadius: 16,
    padding: '36px 40px',
    marginBottom: 32,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: 'var(--shadow-lg)',
  },
  bannerGlow: {
    position: 'absolute',
    right: -60,
    top: -60,
    width: 280,
    height: 280,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(200,151,58,0.2) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  bannerPattern: {
    position: 'absolute',
    inset: 0,
    backgroundImage: `repeating-linear-gradient(
      -45deg,
      transparent,
      transparent 20px,
      rgba(255,255,255,0.02) 20px,
      rgba(255,255,255,0.02) 21px
    )`,
    pointerEvents: 'none',
  },
  bannerGreeting: {
    fontSize: 13,
    color: 'var(--ka-gold-light)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    fontWeight: 600,
    marginBottom: 8,
    position: 'relative',
  },
  bannerTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 36,
    fontWeight: 700,
    color: 'var(--ka-white)',
    marginBottom: 10,
    position: 'relative',
  },
  bannerSubtitle: {
    fontSize: 15,
    color: 'rgba(255,255,255,0.6)',
    fontWeight: 300,
    position: 'relative',
    maxWidth: 500,
    lineHeight: 1.6,
  },
  rolePill: {
    display: 'inline-block',
    marginTop: 16,
    padding: '5px 14px',
    background: 'rgba(200,151,58,0.25)',
    border: '1px solid rgba(200,151,58,0.4)',
    borderRadius: 20,
    fontSize: 12,
    color: 'var(--ka-gold-light)',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    position: 'relative',
  },
  sectionTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 20,
    fontWeight: 600,
    color: 'var(--ka-navy)',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  sectionLine: {
    flex: 1,
    height: 1,
    background: 'var(--ka-gray-200)',
  },
  toolsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: 16,
    marginBottom: 36,
  },
  toolCard: {
    background: 'var(--ka-white)',
    border: '1px solid var(--ka-gray-200)',
    borderRadius: 12,
    padding: '24px 24px 20px',
    transition: 'all 0.2s',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'block',
  },
  toolCardHover: {
    borderColor: 'var(--ka-gold)',
    boxShadow: '0 4px 20px rgba(200,151,58,0.2)',
    transform: 'translateY(-2px)',
  },
  toolIcon: {
    fontSize: 32,
    marginBottom: 12,
    display: 'block',
  },
  toolIconImg: {
    width: 32,
    height: 32,
    objectFit: 'contain',
    marginBottom: 12,
    display: 'block',
  },
  toolName: {
    fontFamily: 'var(--font-display)',
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--ka-navy)',
    marginBottom: 5,
  },
  toolDesc: {
    fontSize: 13,
    color: 'var(--ka-gray-600)',
    lineHeight: 1.5,
  },
  toolArrow: {
    marginTop: 14,
    fontSize: 13,
    color: 'var(--ka-gold)',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  noTools: {
    background: 'var(--ka-white)',
    border: '1px dashed var(--ka-gray-200)',
    borderRadius: 12,
    padding: '40px',
    textAlign: 'center',
    color: 'var(--ka-gray-400)',
    fontSize: 14,
  },
  infoRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
    marginBottom: 36,
  },
  infoCard: {
    background: 'var(--ka-white)',
    border: '1px solid var(--ka-gray-200)',
    borderRadius: 12,
    padding: '20px 24px',
  },
  infoLabel: {
    fontSize: 11,
    color: 'var(--ka-gray-400)',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontWeight: 600,
    marginBottom: 6,
  },
  infoVal: {
    fontFamily: 'var(--font-display)',
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--ka-navy)',
  },
  infoSub: {
    fontSize: 12,
    color: 'var(--ka-gray-400)',
    marginTop: 2,
  },
};

// Render a tool's icon — branches on iconType so image paths render as
// <img> tags rather than being dumped as raw text. Emoji icons keep their
// inline rendering since they're single Unicode characters.
function ToolIcon({ tool }) {
  if (tool.iconType === 'image') {
    return <img src={tool.icon} alt="" style={s.toolIconImg} />;
  }
  return <span style={s.toolIcon}>{tool.icon}</span>;
}

export default function Home() {
  const { profile, isSuperAdmin } = useAuth();
  const { getAccessibleTools } = useAccess();
  const [hoveredTool, setHoveredTool] = React.useState(null);
  const isMobile = useIsMobile();

  const tools = getAccessibleTools();
  const firstName = profile?.displayName?.split(' ')[0] || profile?.email?.split('@')[0] || 'there';

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const term = (() => {
    const m = now.getMonth();
    if (m >= 8) return 'Fall Term';
    if (m >= 4) return 'Spring Term';
    return 'Winter Term';
  })();

  // --- Responsive style variants -------------------------------------------
  const welcomeBanner = {
    ...s.welcomeBanner,
    ...(isMobile ? { padding: '26px 22px', marginBottom: 24, borderRadius: 14 } : {}),
  };
  const bannerTitle = {
    ...s.bannerTitle,
    ...(isMobile ? { fontSize: 28 } : {}),
  };
  const bannerSubtitle = {
    ...s.bannerSubtitle,
    ...(isMobile ? { fontSize: 14 } : {}),
  };
  const infoRow = {
    ...s.infoRow,
    ...(isMobile ? { gridTemplateColumns: '1fr', gap: 12, marginBottom: 28 } : {}),
  };
  const toolsGrid = {
    ...s.toolsGrid,
    ...(isMobile ? { gridTemplateColumns: '1fr', gap: 12 } : {}),
  };
  const toolCard = {
    ...s.toolCard,
    ...(isMobile ? { padding: '18px 18px 16px' } : {}),
  };

  return (
    <div style={s.page}>

      {/* Welcome banner */}
      <div style={welcomeBanner}>
        <div style={s.bannerGlow} />
        <div style={s.bannerPattern} />
        <div style={s.bannerGreeting}>{greeting}</div>
        <div style={bannerTitle}>{firstName}</div>
        <div style={bannerSubtitle}>
          Welcome to the King's Academy Faculty Tool Kit — your central hub for
          school tools and resources.
        </div>
        <div style={s.rolePill}>
          {isSuperAdmin ? '⚡ Super Admin' : (ROLE_LABELS[profile?.role] || profile?.role)}
        </div>
      </div>

      {/* Quick stats */}
      <div style={infoRow}>
        <div style={s.infoCard}>
          <div style={s.infoLabel}>Today</div>
          <div style={s.infoVal}>
            {now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
          </div>
          <div style={s.infoSub}>Academic Year 2025–2026</div>
        </div>
        <div style={s.infoCard}>
          <div style={s.infoLabel}>Current Term</div>
          <div style={s.infoVal}>{term}</div>
          <div style={s.infoSub}>King's Academy, Madaba</div>
        </div>
        <div style={s.infoCard}>
          <div style={s.infoLabel}>Tools Available</div>
          <div style={s.infoVal}>{tools.length}</div>
          <div style={s.infoSub}>
            {tools.length === 0 ? 'Contact admin for access' : 'Click below to launch'}
          </div>
        </div>
      </div>

      {/* Tools */}
      <div style={s.sectionTitle}>
        Your Tools
        <div style={s.sectionLine} />
      </div>

      {tools.length > 0 ? (
        <div style={toolsGrid}>
          {tools.map(tool => (
            <Link
              key={tool.id}
              to={tool.path}
              style={{
                ...toolCard,
                ...(hoveredTool === tool.id ? s.toolCardHover : {}),
              }}
              onMouseEnter={() => setHoveredTool(tool.id)}
              onMouseLeave={() => setHoveredTool(null)}
            >
              <ToolIcon tool={tool} />
              <div style={s.toolName}>{tool.label}</div>
              <div style={s.toolDesc}>{tool.description}</div>
              <div style={s.toolArrow}>Launch →</div>
            </Link>
          ))}
        </div>
      ) : (
        <div style={s.noTools}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🔐</div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>No tools assigned yet</div>
          <div>Ask an administrator to grant you access to the tools you need.</div>
        </div>
      )}

    </div>
  );
}